// Pointer, wheel, drag-and-drop and scrubbing on the viewer (F-06.5, F-05.2, F-01.10).

import { isSequenceImage } from '../core/sequence';
import { looksLikeMediaLinks, refFromDrag } from '../core/remote-media';
import type { App } from '../app/app';
import type { Pane } from '../app/view';
import type { Point, SourceId } from '../core/types';
import { $, $$, toast } from './dom';
import { t } from '../i18n';

type Drag =
  | { type: 'cross'; pane: Pane }
  | { type: 'panZoom'; last: Point; pane: Pane; pick?: { start: Point; src: SourceId; at: Point; near: number } }
  | { type: 'panTop'; last: Point }
  | { type: 'wipe'; pane: Pane };

export function devPoint(canvas: HTMLCanvasElement, e: { clientX: number; clientY: number }): Point {
  const r = canvas.getBoundingClientRect();
  const k = canvas.width / r.width;
  return { x: (e.clientX - r.left) * k, y: (e.clientY - r.top) * k };
}

export function initInteractions(app: App): void {
  const cv = app.view.canvas;
  const view = app.view;
  let drag: Drag | null = null;
  let wheelAcc = 0;
  const clampA = (p: Point): Point => {
    const f = view.frameA();
    return { x: Math.max(0, Math.min(f.w - 0.001, p.x)), y: Math.max(0, Math.min(f.h - 0.001, p.y)) };
  };
  const nearWipe = (pt: Point, p: Pane) => {
    const L = view.wipeLine(p.rect);
    const nx = Math.cos(L.theta) / p.rect.w;
    const ny = Math.sin(L.theta) / p.rect.h;
    const len = Math.hypot(nx, ny) || 1;
    return Math.abs(((pt.x - L.px) * nx + (pt.y - L.py) * ny) / len) < 10 * view.dpr;
  };
  const setCrossFromPoint = (pt: Point, p: Pane) => {
    const a = clampA(view.paneToA(pt, p));
    // Snap to the centre of a comparison-grid pixel (a B pixel when the grid is A × k, D-37).
    const k = app.project.compareScale;
    app.setCrosshair({ x: (Math.floor(a.x * k) + 0.5) / k, y: (Math.floor(a.y * k) + 0.5) / k });
  };

  cv.addEventListener('contextmenu', (e) => e.preventDefault());
  // F-04.5: double-click a B in the quad layout to compare it one-to-one with A.
  cv.addEventListener('dblclick', (e) => {
    const p = view.paneAt(devPoint(cv, e));
    const l = app.project.layout.layout;
    if (p && p.kind !== 'empty' && p.src !== 'A' && l === 'quad') app.focus(p.src);
  });
  cv.addEventListener('pointerdown', (e) => {
    const pt = devPoint(cv, e);
    const p = view.paneAt(pt);
    if (!p || p.kind === 'empty') return;
    cv.setPointerCapture(e.pointerId);
    if (e.button === 1 || e.button === 2) {
      if (p.kind !== 'zoom' && app.project.layout.view.zoomPercent !== 'fit') drag = { type: 'panTop', last: pt };
      return;
    }
    // Two-point alignment (F-03.11): a click on A's or the version's picture marks a point; in a zoom pane a click
    // that does not move marks it (a drag still pans), for placing it precisely.
    const pick = app.state.ui.alignPick;
    if (pick && e.button === 0 && (p.src === 'A' || p.src === pick.id)) {
      const at = clampA(view.paneToA(pt, p));
      const near = (24 * view.dpr) / (p.view.s * view.k);
      if (p.kind === 'zoom') {
        drag = { type: 'panZoom', last: pt, pane: p, pick: { start: pt, src: p.src, at, near } };
        return;
      }
      app.addAlignPoint(p.src, at, near);
      setCrossFromPoint(pt, p);
      return;
    }
    if (p.kind === 'zoom') {
      drag = { type: 'panZoom', last: pt, pane: p };
      cv.classList.add('grabbing');
      return;
    }
    if (p.kind === 'composite' && app.view.compareMode() === 'wipe' && nearWipe(pt, p)) {
      drag = { type: 'wipe', pane: p };
      return;
    }
    drag = { type: 'cross', pane: p };
    setCrossFromPoint(pt, p);
  });

  cv.addEventListener('pointermove', (e) => {
    const pt = devPoint(cv, e);
    if (!drag) {
      const p = view.paneAt(pt);
      cv.classList.toggle('grab', !!p && p.kind === 'zoom');
      cv.classList.toggle('ew', !!p && p.kind === 'composite' && app.view.compareMode() === 'wipe' && nearWipe(pt, p));
      return;
    }
    if (drag.type === 'cross') {
      const d0 = drag;
      const p = view.panes.find((q) => q.kind === d0.pane.kind && q.src === d0.pane.src) ?? d0.pane;
      setCrossFromPoint(pt, p);
    } else if (drag.type === 'panZoom') {
      const n = app.project.layout.crosshair.zoomPercent / 100;
      const k = app.project.compareScale;
      const c = app.project.layout.crosshair.position;
      app.setCrosshair({ x: c.x - (pt.x - drag.last.x) / n / k, y: c.y - (pt.y - drag.last.y) / n / k });
      drag.last = pt;
    } else if (drag.type === 'panTop') {
      const g = view.grid();
      const s = (app.project.layout.view.zoomPercent as number) / 100;
      const dx = (pt.x - drag.last.x) / s / g.w;
      const dy = (pt.y - drag.last.y) / s / g.h;
      app.store.project((pp) => {
        const c = pp.layout.view.center;
        pp.layout.view.center = { x: Math.max(0, Math.min(1, c.x - dx)), y: Math.max(0, Math.min(1, c.y - dy)) };
      }, { undo: false });
      drag.last = pt;
    } else if (drag.type === 'wipe') {
      const r = drag.pane.rect;
      const L = app.project.layout;
      const th = (L.wipe.angleDeg * Math.PI) / 180;
      // Position along the normal in pane-normalised units (matches the shader's split).
      const u = (pt.x - r.x) / r.w - 0.5;
      const v = (pt.y - r.y) / r.h - 0.5;
      const pos = Math.max(0, Math.min(1, u * Math.cos(th) + v * Math.sin(th) + 0.5));
      app.store.project((pp) => (pp.layout.wipe.position = pos), { undo: false });
    }
  });

  const endDrag = (e?: PointerEvent) => {
    const d = drag;
    if (e && d?.type === 'panZoom' && d.pick) {
      const pt = devPoint(cv, e);
      if (Math.hypot(pt.x - d.pick.start.x, pt.y - d.pick.start.y) < 4 * view.dpr) app.addAlignPoint(d.pick.src, d.pick.at, d.pick.near);
    }
    drag = null;
    cv.classList.remove('grabbing');
  };
  cv.addEventListener('pointerup', endDrag);
  cv.addEventListener('pointercancel', () => endDrag());
  cv.addEventListener(
    'wheel',
    (e) => {
      const p = view.paneAt(devPoint(cv, e));
      if (!p || p.kind !== 'zoom') return;
      e.preventDefault();
      wheelAcc += e.deltaY;
      if (Math.abs(wheelAcc) >= 60) {
        app.zoomStep(wheelAcc < 0 ? 1 : -1);
        wheelAcc = 0;
      }
    },
    { passive: false },
  );

  // Splitter between the top panes and the zoom panes (F-04.2).
  const splitter = $('#splitter');
  splitter.addEventListener('pointerdown', (e) => {
    splitter.setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent) => {
      const r = cv.getBoundingClientRect();
      const ratio = Math.max(0.2, Math.min(0.8, (ev.clientY - r.top) / r.height));
      app.store.project((p) => (p.layout.zoomPaneRatio = ratio), { undo: false });
    };
    const up = () => {
      splitter.removeEventListener('pointermove', move);
      splitter.removeEventListener('pointerup', up);
    };
    splitter.addEventListener('pointermove', move);
    splitter.addEventListener('pointerup', up);
  });

  // Drop zones: file pickers and drag & drop (F-01.10). Handles are kept for one-click re-open (D-28).
  let pendingPick: SourceId | undefined;
  // The panes' "⋯" buttons sit over the canvas: keep their presses away from crosshair / drawing drags.
  $('#overlays').addEventListener('pointerdown', (e) => {
    if ((e.target as HTMLElement).closest('.pane-more, .align-hint')) e.stopPropagation();
  });
  $('#overlays').addEventListener('click', async (e) => {
    const more = (e.target as HTMLElement).closest<HTMLElement>('[data-more]');
    if (more) {
      const r = more.getBoundingClientRect();
      const { openSourceMenu } = await import('./source-menu');
      openSourceMenu(app, more.dataset.more as SourceId, r.left, r.bottom + 4);
      return;
    }
    // Alignment reminder (F-03.10): apply the suggestion, or stop pointing it out for this version.
    const apply = (e.target as HTMLElement).closest<HTMLElement>('[data-align-apply]');
    if (apply) {
      app.applySuggestion(apply.dataset.alignApply as SourceId);
      return;
    }
    const ignore = (e.target as HTMLElement).closest<HTMLElement>('[data-align-ignore]');
    if (ignore) {
      app.alignIgnored.add(ignore.dataset.alignIgnore as SourceId);
      app.store.notify();
      return;
    }
    const link = (e.target as HTMLElement).closest<HTMLElement>('[data-link]');
    if (link) {
      await pasteLinks(app, link.dataset.link as SourceId);
      return;
    }
    const dir = (e.target as HTMLElement).closest<HTMLElement>('[data-pickdir]');
    if (dir) {
      await pickSequenceFolder(app, dir.dataset.pickdir as SourceId);
      return;
    }
    const pick = (e.target as HTMLElement).closest<HTMLElement>('[data-pick]');
    if (!pick) return;
    pendingPick = pick.dataset.pick as SourceId;
    await pickVideos(app, pendingPick);
  });
  $<HTMLInputElement>('#fileInput').addEventListener('change', (e) => {
    const input = e.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (files.length) void app.loadFiles(files.map((file) => ({ file })), pendingPick);
  });
  $<HTMLInputElement>('#projectInput').addEventListener('change', (e) => {
    const input = e.target as HTMLInputElement;
    const f = input.files?.[0];
    input.value = '';
    if (f) void app.openProjectFile(f);
  });

  const viewer = $('#viewer');
  const dropTarget = (e: DragEvent): SourceId | undefined => {
    const pt = devPoint(cv, e);
    const p = view.paneAt(pt);
    if (!p) return undefined;
    // One-to-one: the composite stands for A (left half) and the version; in the quad it is the version's own pane.
    if (p.kind === 'composite' && app.project.layout.layout !== 'quad') return pt.x < p.rect.x + p.rect.w / 2 ? 'A' : p.src;
    return p.src;
  };
  viewer.addEventListener('dragover', (e) => {
    e.preventDefault();
    const p = view.paneAt(devPoint(cv, e));
    $$('.pane-ov').forEach((el) => el.classList.toggle('drag-target', !!p && Number(el.dataset.i) === view.panes.indexOf(p)));
  });
  viewer.addEventListener('dragleave', () => $$('.pane-ov').forEach((el) => el.classList.remove('drag-target')));
  viewer.addEventListener('drop', async (e) => {
    e.preventDefault();
    $$('.pane-ov').forEach((el) => el.classList.remove('drag-target'));
    // A video dragged from moonland (or a link from another page): downloaded into the pane under the mouse (F-01.13).
    const dt = e.dataTransfer;
    if (dt && !Array.from(dt.types).includes('Files')) {
      const got = refFromDrag((type) => dt.getData(type), Array.from(dt.types));
      if (got && 'ref' in got) await app.loadRemote([got.ref], dropTarget(e) ?? app.firstEmptySlot());
      else toast(t(got ? 'toast.link.dragImage' : 'toast.link.dragNone'), 5000);
      return;
    }
    const { files: items, dirs } = await dataTransferFiles(dt);
    // A dropped folder is an image sequence (F-01.12); one item (file, folder or sequence) goes to the pane under the mouse.
    const one = items.length + dirs.length === 1 || (dirs.length === 0 && items.length > 1 && items.every((i) => isSequenceImage(i.file.name)));
    if (items.length || dirs.length) await app.loadFiles(items, one ? dropTarget(e) : undefined, dirs);
  });
  // Pasting moonland / Google Drive links anywhere outside a text field downloads and loads them (F-01.13).
  document.addEventListener('paste', (e) => {
    const el = e.target as HTMLElement | null;
    if (el && (el.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]') || el.isContentEditable)) return;
    const text = e.clipboardData?.getData('text/plain') ?? '';
    if (!looksLikeMediaLinks(text)) return;
    e.preventDefault();
    void app.loadLinks(text);
  });
  window.addEventListener('dragover', (e) => e.preventDefault());
  window.addEventListener('drop', (e) => e.preventDefault());

  // Timeline scrubbing.
  const track = $('#track');
  const scrub = (e: PointerEvent) => {
    const r = track.getBoundingClientRect();
    const tl = app.sources.timeline;
    app.player.seek(Math.floor(((e.clientX - r.left) / r.width) * tl.totalTicks));
  };
  track.addEventListener('pointerdown', (e) => {
    track.setPointerCapture(e.pointerId);
    if (app.player.playing) app.player.pause();
    scrub(e);
    const move = (ev: PointerEvent) => scrub(ev);
    const up = () => {
      track.removeEventListener('pointermove', move);
      track.removeEventListener('pointerup', up);
    };
    track.addEventListener('pointermove', move);
    track.addEventListener('pointerup', up);
  });
}

/** Asks for moonland / Google Drive links and loads them from the given slot on (F-01.13). */
export async function pasteLinks(app: App, target?: SourceId): Promise<void> {
  const { promptText } = await import('./project-tree');
  const text = await promptText(t('link.title'), t('link.label'), '');
  if (text) await app.loadLinks(text, target);
}

/** Picks a folder holding a PNG / EXR sequence (F-01.12); its handle re-opens it later. */
export async function pickSequenceFolder(app: App, target?: SourceId): Promise<void> {
  try {
    const { fsAccess } = await import('../app/persist');
    const dir = await fsAccess.pickDirectory('read');
    if (!dir) {
      toast(t('toast.folderUnsupported'), 5000);
      return;
    }
    await app.loadFiles([], target, [dir]);
  } catch (e) {
    if ((e as DOMException).name !== 'AbortError') toast(t('toast.folderFailed', { detail: String(e) }), 5000);
  }
}

export async function pickVideos(app: App, target?: SourceId): Promise<void> {
  try {
    const { fsAccess } = await import('../app/persist');
    const r = fsAccess.supported ? await fsAccess.openVideos() : null;
    if (r) await app.loadFiles(r, target);
    else $<HTMLInputElement>('#fileInput').click();
  } catch (e) {
    if ((e as DOMException).name !== 'AbortError') $<HTMLInputElement>('#fileInput').click();
  }
}

type HandleItem = DataTransferItem & { getAsFileSystemHandle?: () => Promise<FileSystemHandle | null> };

async function dataTransferFiles(dt: DataTransfer | null): Promise<{ files: { file: File; handle?: FileSystemFileHandle }[]; dirs: FileSystemDirectoryHandle[] }> {
  if (!dt) return { files: [], dirs: [] };
  const items = Array.from(dt.items ?? []).filter((i) => i.kind === 'file') as HandleItem[];
  // Handles must be requested synchronously inside the drop event before awaiting anything.
  const handlePromises = items.map((i) => (i.getAsFileSystemHandle ? i.getAsFileSystemHandle().catch(() => null) : Promise.resolve(null)));
  const files = items.map((i) => i.getAsFile());
  const handles = await Promise.all(handlePromises);
  const out: { file: File; handle?: FileSystemFileHandle }[] = [];
  const dirs: FileSystemDirectoryHandle[] = [];
  files.forEach((f, i) => {
    const h = handles[i];
    if (h?.kind === 'directory') {
      dirs.push(h as FileSystemDirectoryHandle);
      return;
    }
    if (!f) return;
    out.push({ file: f, handle: h && h.kind === 'file' ? (h as FileSystemFileHandle) : undefined });
  });
  if (!out.length && !dirs.length) for (const f of Array.from(dt.files)) out.push({ file: f });
  return { files: out, dirs };
}
